import type { Address, Coordinates, MonthPeriod, Month, BandInterest, InterestLevel, Band, VenueStatus } from '../../types';

/** Address fields whose change invalidates stored (commune-level) coordinates. A street edit
 *  cannot falsify a commune centroid, so it is deliberately absent. */
export const COORD_INVALIDATING_FIELDS: ReadonlySet<keyof Address> = new Set([
  'city',
  'zip_code',
  'administrative_region_1',
  'administrative_region_2',
  'country',
]);

export interface VenueFormData {
  name: string;
  url?: string;
  type: 'concert_hall' | 'bar' | 'concert_bar' | 'jazz_club' | 'restaurant' | 'hotel' | 'city_concert_hall' | 'festival' | 'association' | 'city' | 'comite_des_fetes' | 'competition' | 'mentoring' | 'booking_agency' | 'other';
  commentary?: string;
  address?: Address;
  /** `null` clears any stored coordinates server-side (see VenueService.update_venue). */
  coordinates?: Coordinates | null;
  styles: string[];
  previous_artists: string[];
  interests: BandInterest[];
  status: VenueStatus;
  contact_ids?: string[];
  /** The venue's gatekeepers among contact_ids — a role at this venue. Omit to leave them as they are. */
  gatekeeper_contact_ids?: string[];
  ideal_contact_period?: MonthPeriod;
  expect_answer_by?: MonthPeriod;
  concert_period?: MonthPeriod;
}

export const venueTypes = [
  { value: 'concert_hall', label: 'Concert Hall' },
  { value: 'bar', label: 'Bar' },
  { value: 'concert_bar', label: 'Concert Bar' },
  { value: 'jazz_club', label: 'Jazz Club' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'city_concert_hall', label: 'City Concert Hall' },
  { value: 'festival', label: 'Festival' },
  { value: 'association', label: 'Association' },
  { value: 'city', label: 'City' },
  { value: 'comite_des_fetes', label: 'Comité des fêtes' },
  { value: 'competition', label: 'Competition' },
  { value: 'mentoring', label: 'Mentoring' },
  { value: 'booking_agency', label: 'Booking Agency' },
  { value: 'other', label: 'Other' },
] as const;

export const DEFAULT_VENUE_TYPE: VenueFormData['type'] = 'bar';

/** A name containing "comite" (Comité des fêtes, Comité d'animation…) means a comité des fêtes;
 *  one containing "fest" (Festival, Fest'Arts, Woodfest…) means a festival. "comite" is tested
 *  first, so a "Comité des Festivités" stays a comité rather than becoming a festival.
 *  Accents are stripped so "Fèstival" and "Comité" match too.
 *  Returns `null` when nothing is inferred. */
export function inferVenueTypeFromName(name: string): VenueFormData['type'] | null {
  const normalized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  if (normalized.includes('comite')) return 'comite_des_fetes';
  return normalized.includes('fest') ? 'festival' : null;
}

export const statusOptions = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'no_concerts', label: 'No Concerts' },
  { value: 'unknown', label: 'Unknown' },
] as const;

/** The level a venue's pause hangs off: the two are one state (docs/VENUE_PAUSE.md). */
export const PAUSE_LEVEL: InterestLevel = 'not_interested_for_now';

/**
 * The interest levels as badges — the styled twin of `interestLevelOptions` below, which carries
 * the longer labels the filters use.
 */
export const INTEREST_LEVELS: { value: InterestLevel; label: string; color: string; bgClass: string }[] = [
  { value: 'no_information', label: 'No Info', color: 'text-dark-text-muted', bgClass: 'bg-dark-elevated border-dark-border' },
  { value: 'not_interested', label: 'Not Interested', color: 'text-red-400', bgClass: 'bg-red-900/30 border-red-700' },
  { value: 'not_interested_for_now', label: 'Not Now', color: 'text-orange-400', bgClass: 'bg-orange-900/30 border-orange-700' },
  { value: 'mildly_interested', label: 'Mildly', color: 'text-blue-400', bgClass: 'bg-blue-900/30 border-blue-700' },
  { value: 'very_interested', label: 'Very Interested', color: 'text-green-400', bgClass: 'bg-green-900/30 border-green-700' },
  { value: 'concert_proposal', label: 'Proposal', color: 'text-primary-400', bgClass: 'bg-primary-900/30 border-primary-700' },
];

export function getInterestConfig(level: InterestLevel) {
  return INTEREST_LEVELS.find(l => l.value === level) || INTEREST_LEVELS[0];
}

export const interestLevelOptions: { value: InterestLevel; label: string; short: string }[] = [
  { value: 'no_information', label: 'No Information', short: 'No Info' },
  { value: 'not_interested', label: 'Not Interested', short: 'No' },
  { value: 'not_interested_for_now', label: 'Not For Now', short: 'Not Now' },
  { value: 'mildly_interested', label: 'Mildly Interested', short: 'Mild' },
  { value: 'very_interested', label: 'Very Interested', short: 'Very' },
  { value: 'concert_proposal', label: 'Concert Proposal', short: 'Proposal' },
];

export const musicStyles = [
  'Rock', 'Jazz', 'Blues', 'Folk', 'Classical', 'Pop',
  'Electronic', 'Metal', 'Punk', 'Hip Hop', 'Country', 'Reggae'
];

export const MONTHS: { value: Month; label: string; short: string }[] = [
  { value: 'january', label: 'January', short: 'Jan' },
  { value: 'february', label: 'February', short: 'Feb' },
  { value: 'march', label: 'March', short: 'Mar' },
  { value: 'april', label: 'April', short: 'Apr' },
  { value: 'may', label: 'May', short: 'May' },
  { value: 'june', label: 'June', short: 'Jun' },
  { value: 'july', label: 'July', short: 'Jul' },
  { value: 'august', label: 'August', short: 'Aug' },
  { value: 'september', label: 'September', short: 'Sep' },
  { value: 'october', label: 'October', short: 'Oct' },
  { value: 'november', label: 'November', short: 'Nov' },
  { value: 'december', label: 'December', short: 'Dec' },
];

export const emptyMonthPeriod = (): MonthPeriod => ({ all_year: false, months: [] });

/** Build interests array ensuring Overall + all bands have entries. */
export function buildInterestsForForm(existing: BandInterest[], bands: Band[]): BandInterest[] {
  const map = new Map<string, BandInterest>();
  for (const entry of existing) {
    map.set(entry.band_id ?? 'overall', entry);
  }
  const result: BandInterest[] = [];
  // Overall first
  result.push(map.get('overall') ?? { band_id: null, level: 'no_information' });
  // Then each band
  for (const band of bands) {
    result.push(map.get(band._id) ?? { band_id: band._id, level: 'no_information' });
  }
  return result;
}
